import { extractArtifactFromMarkdown as extractPersistedArtifact } from '../../../../agents/context/artifact-source.js';
import {
  commandRequest,
  type UiCommandRequest,
} from '../../../../agents/context/ui-command-registry.js';
import { EntityStatusChip } from '../../../../components/entity-detail/EntityStatusChip.js';
import React, { useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import {
  FileText,
  FileCode,
  Network,
  ListTodo,
  Copy,
  Check,
  Download,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ListPlus,
  StickyNote,
  Eye,
  Code2,
} from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../../i18n/index.js';
import type { ArtifactContent } from '../ArtifactCanvas.js';
import { BoundedMarkdownRenderer } from '../BoundedMarkdownRenderer.js';
import { MermaidSnippet } from './MermaidSnippet.js';
import { AgentTodoSnippet } from './AgentTodoSnippet.js';

export interface ArtifactSnippetProps {
  artifact: ArtifactContent;
  onOpenCanvas?: ((artifact: ArtifactContent) => void) | undefined;
  sourceMessageId?: string | undefined;
  artifactIndex?: number | undefined;
  dealId?: string | undefined;
  onExecuteCommand?: ((command: UiCommandRequest) => void) | undefined;
  defaultExpanded?: boolean | undefined;
}

export function extractArtifactFromMarkdown(content: string): ArtifactContent | null {
  return extractPersistedArtifact(
    content,
    (key) => i18n.t(key),
    (type) => `art-${type}-${Math.random().toString(36).slice(2, 8)}`,
  );
}

export function downloadArtifactFile(
  title: string,
  content: string,
  type: ArtifactContent['type'],
): void {
  let extension = 'md';
  let mimeType = 'text/markdown';

  if (type === 'html') {
    extension = 'html';
    mimeType = 'text/html';
  } else if (type === 'mermaid') {
    extension = 'mmd';
    mimeType = 'text/plain';
  } else if (type === 'todo') {
    extension = 'txt';
    mimeType = 'text/plain';
  }

  const safeFilename = `${title.trim().replace(/[^\w\s-]/gi, '_') || 'artifact'}.${extension}`;
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const ArtifactSnippet: React.FC<ArtifactSnippetProps> = ({
  artifact,
  onOpenCanvas,
  sourceMessageId,
  artifactIndex = 0,
  dealId,
  onExecuteCommand,
  defaultExpanded = true,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const [activeTab, setActiveTab] = useState<'preview' | 'code'>('preview');
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleDownload = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    downloadArtifactFile(artifact.title, artifact.content, artifact.type);
  };

  const handleOpenSideCanvas = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onOpenCanvas?.(artifact);
  };

  const handleCreateNotePrompt = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!dealId) {
      message.info(t('crm.ai_chat.handoff.clarify_deal'));
      return;
    }
    if (!sourceMessageId) return;
    onExecuteCommand?.(
      commandRequest('createNoteFromArtifact', { sourceMessageId, artifactIndex, dealId }),
    );
  };

  const handleCreateTasksPrompt = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!sourceMessageId) return;
    onExecuteCommand?.(
      commandRequest('createTasksFromArtifact', {
        sourceMessageId,
        artifactIndex,
        ...(dealId ? { dealId } : {}),
      }),
    );
  };

  const typeConfig = {
    document: {
      icon: <FileText size={16} className="text-amber-600 dark:text-amber-400" />,
      bgIcon: 'bg-amber-50 border-amber-200/80 dark:bg-amber-950/30 dark:border-amber-800/60',
      label: 'MD',
      typeName: t('crm.ai_chat.artifact.type_doc'),
    },
    html: {
      icon: <FileCode size={16} className="text-emerald-600 dark:text-emerald-400" />,
      bgIcon:
        'bg-emerald-50 border-emerald-200/80 dark:bg-emerald-950/30 dark:border-emerald-800/60',
      label: 'HTML',
      typeName: t('crm.ai_chat.artifact.type_html'),
    },
    mermaid: {
      icon: <Network size={16} className="text-blue-600 dark:text-blue-400" />,
      bgIcon: 'bg-blue-50 border-blue-200/80 dark:bg-blue-950/30 dark:border-blue-800/60',
      label: 'DIAGRAM',
      typeName: t('crm.ai_chat.artifact.type_mermaid'),
    },
    todo: {
      icon: <ListTodo size={16} className="text-amber-600 dark:text-amber-400" />,
      bgIcon: 'bg-amber-50 border-amber-200/80 dark:bg-amber-950/30 dark:border-amber-800/60',
      label: 'TODO',
      typeName: t('crm.ai_chat.artifact.type_todo'),
    },
  }[artifact.type] ?? {
    icon: <FileText size={16} className="text-stone-600 dark:text-stone-400" />,
    bgIcon: 'bg-stone-50 border-stone-200/80 dark:bg-stone-800/50 dark:border-stone-700/80',
    label: 'FILE',
    typeName: t('crm.ai_chat.artifact.type_doc'),
  };

  const lineCount = artifact.content.split('\n').length;

  return (
    <div
      data-testid="in-chat-artifact-card"
      className="my-2.5 overflow-hidden rounded-2xl border border-stone-200/90 bg-white shadow-2xs transition-all hover:border-amber-400/80 hover:shadow-xs dark:border-stone-700/80 dark:bg-stone-900/60"
    >
      {/* ─── Compact Header & Quick Bar ──────────────────────────── */}
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex cursor-pointer items-center justify-between gap-3 bg-[var(--color-surface)] px-3.5 py-2.5 transition hover:bg-stone-100/60 select-none border-b border-stone-100 dark:border-stone-800 dark:hover:bg-stone-800/60"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${typeConfig.bgIcon} shadow-2xs`}
          >
            {typeConfig.icon}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-xs font-bold text-stone-900">{artifact.title}</span>
              <EntityStatusChip
                tone="default"
                className="m-0 rounded-md px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider leading-4"
              >
                {typeConfig.label}
              </EntityStatusChip>
              <span className="text-[10px] text-stone-400 font-medium">
                {lineCount} {t('crm.ai_chat.artifact.lines')}
              </span>
            </div>
            {artifact.subtitle && (
              <p className="truncate text-[11px] text-stone-500">{artifact.subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Tooltip
            title={copied ? t('crm.ai_chat.artifact.copied') : t('crm.ai_chat.artifact.copy')}
          >
            <Button
              type="text"
              size="small"
              aria-label={
                copied ? t('crm.ai_chat.artifact.copied') : t('crm.ai_chat.artifact.copy')
              }
              icon={
                copied ? (
                  <Check size={13} className="text-emerald-600" />
                ) : (
                  <Copy size={13} className="text-stone-500" />
                )
              }
              onClick={handleCopy}
              className="h-7 w-7 rounded-lg hover:bg-white dark:hover:bg-stone-800"
            />
          </Tooltip>

          <Tooltip title={t('crm.ai_chat.artifact.download')}>
            <Button
              type="text"
              size="small"
              aria-label={t('crm.ai_chat.artifact.download')}
              icon={<Download size={13} className="text-stone-500" />}
              onClick={handleDownload}
              className="h-7 w-7 rounded-lg hover:bg-white dark:hover:bg-stone-800"
            />
          </Tooltip>

          {onOpenCanvas && (
            <Tooltip title={t('crm.ai_chat.artifact.open_side_canvas')}>
              <Button
                type="text"
                size="small"
                aria-label={t('crm.ai_chat.artifact.open_side_canvas')}
                icon={<ExternalLink size={13} className="text-amber-600" />}
                onClick={handleOpenSideCanvas}
                className="h-7 w-7 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30"
              />
            </Tooltip>
          )}

          <Button
            type="text"
            size="small"
            aria-label={
              isExpanded ? t('crm.ai_chat.artifact.collapse') : t('crm.ai_chat.artifact.expand')
            }
            icon={
              isExpanded ? (
                <ChevronUp size={14} className="text-stone-600" />
              ) : (
                <ChevronDown size={14} className="text-stone-600" />
              )
            }
            className="h-7 w-7 rounded-lg hover:bg-white dark:hover:bg-stone-800 ml-0.5"
          />
        </div>
      </div>

      {/* ─── Expandable Rich Canvas View ──────────────────────────── */}
      {isExpanded && (
        <div className="bg-white dark:bg-stone-900/60">
          {/* Mini Tab switcher inside Card */}
          <div className="flex items-center justify-between border-b border-stone-100 bg-[var(--color-surface)] px-3.5 py-1.5 text-xs dark:border-stone-800">
            <div className="flex items-center gap-1 rounded-lg bg-stone-200/50 p-0.5 dark:bg-stone-800/50">
              <button
                type="button"
                onClick={() => setActiveTab('preview')}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition cursor-pointer ${
                  activeTab === 'preview'
                    ? 'bg-white text-stone-900 shadow-2xs dark:bg-stone-700 dark:text-stone-100'
                    : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'
                }`}
              >
                <Eye size={12} />
                <span>{t('crm.ai_chat.artifact.preview')}</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('code')}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition cursor-pointer ${
                  activeTab === 'code'
                    ? 'bg-white text-stone-900 shadow-2xs dark:bg-stone-700 dark:text-stone-100'
                    : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'
                }`}
              >
                <Code2 size={12} />
                <span>{t('crm.ai_chat.artifact.source')}</span>
              </button>
            </div>

            <span className="text-[11px] text-stone-400 font-medium">{typeConfig.typeName}</span>
          </div>

          {/* Tab Body */}
          <div className="p-3.5 text-xs text-stone-800 dark:text-stone-200">
            {activeTab === 'preview' ? (
              <div>
                {artifact.type === 'document' && (
                  <div className="rounded-xl border border-stone-100 bg-[var(--color-surface)] p-3.5 prose prose-stone max-w-none text-xs leading-relaxed max-h-96 overflow-y-auto crm-microscroll dark:border-stone-800">
                    <BoundedMarkdownRenderer content={artifact.content} isStreaming={false} />
                  </div>
                )}

                {artifact.type === 'html' && (
                  <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-2xs dark:border-stone-700/80 dark:bg-stone-900/60">
                    <iframe
                      title={artifact.title}
                      srcDoc={artifact.content}
                      sandbox="allow-scripts"
                      className="h-80 w-full border-none"
                    />
                  </div>
                )}

                {artifact.type === 'mermaid' && (
                  <MermaidSnippet
                    code={artifact.content}
                    title={artifact.title}
                    onViewCanvas={onOpenCanvas ? () => onOpenCanvas(artifact) : undefined}
                  />
                )}

                {artifact.type === 'todo' && (
                  <AgentTodoSnippet
                    title={artifact.title}
                    items={artifact.todoItems ?? []}
                    onViewCanvas={onOpenCanvas ? () => onOpenCanvas(artifact) : undefined}
                  />
                )}
              </div>
            ) : (
              <div className="relative overflow-hidden rounded-xl border border-stone-800 bg-[#1c1917] p-3 text-stone-100 font-mono text-[11.5px] leading-relaxed">
                <Button
                  size="small"
                  icon={
                    copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />
                  }
                  onClick={handleCopy}
                  className="absolute right-2 top-2 border-stone-700 bg-stone-800 text-stone-200 hover:bg-stone-700 hover:text-white"
                >
                  {copied ? t('crm.ai_chat.artifact.copied') : t('crm.ai_chat.artifact.copy')}
                </Button>
                <pre className="overflow-x-auto pr-16 crm-microscroll max-h-80 whitespace-pre-wrap">
                  {artifact.content}
                </pre>
              </div>
            )}
          </div>

          {/* Direct Interactive CRM Actions Footer */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-stone-100 bg-[var(--color-surface)] px-3.5 py-2 dark:border-stone-800">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button
                size="small"
                icon={<StickyNote size={12} className="text-amber-600" />}
                onClick={handleCreateNotePrompt}
                className="rounded-lg text-xs font-medium border-stone-200 hover:border-amber-300 hover:bg-amber-50/60 dark:border-stone-700 dark:hover:border-amber-800 dark:hover:bg-amber-950/30"
              >
                {t('crm.ai_chat.artifact.attach_note')}
              </Button>

              <Button
                size="small"
                icon={<ListPlus size={12} className="text-blue-600" />}
                onClick={handleCreateTasksPrompt}
                className="rounded-lg text-xs font-medium border-stone-200 hover:border-blue-300 hover:bg-blue-50/60 dark:border-stone-700 dark:hover:border-blue-800 dark:hover:bg-blue-950/30"
              >
                {t('crm.ai_chat.artifact.create_crm_tasks')}
              </Button>
            </div>

            {onOpenCanvas && (
              <button
                type="button"
                onClick={handleOpenSideCanvas}
                className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-800 transition cursor-pointer dark:text-amber-400 dark:hover:text-amber-300"
              >
                <span>{t('crm.ai_chat.artifact.open_in_canvas_mode')}</span>
                <ExternalLink size={12} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
